import type { Partner } from '@dashboard/types/partner'
import type { Dimension } from '@dashboard/types/config'
import type { QuestionTypeDefinition } from '../services/questionCatalog'
import makePartnerSource from './sources/partner'
import referrerSource from './sources/referrer'
import segmentsSource from './sources/segments'
import pageviewsSource from './sources/pageviews'
import utmSource from './sources/utm'
import querySource from './sources/query'
import jsdataSource from './sources/jsdata'
import wordpressSource from './sources/wordpress'
import deviceSource from './sources/device'
import dateSource from './sources/date'
import scoreSource from './sources/score'
import rightmessageSource from './sources/rightmessage'
import locationSource from './sources/location'
import answersSource from './sources/answers'

export type LogicDefinitionContext = {
  ignoreSegmentGroups: Dimension[]
  offerFunnelId?: string
  usageContext?: 'segment' | 'flow' | 'widget-targeting'
}

/** One condition trigger; input questions carry their type for the picker's icon and type label. */
export interface TriggerOption {
  label: string
  value: string
  /** Short plain-language hint shown beside triggers that aren't questions. */
  description?: string
  questionType?: QuestionTypeDefinition
}
/** A category of triggers, as listed on the left of the trigger picker. */
export interface TriggerGroup {
  group: string
  name: string
  options: TriggerOption[]
}

const sources: Record<string, { label: string; source: (context?: any) => any }> = {
  referrer: { label: 'Referred By', source: referrerSource },
  pageviews: { label: 'On-Site Behavior', source: pageviewsSource },
  segments: { label: 'Segments / Questions', source: segmentsSource },
  answers: { label: 'Segments / Questions', source: answersSource },
  utm: { label: 'UTM Parameters', source: utmSource },
  query: { label: 'Query Parameters', source: querySource },
  rightmessage: { label: 'RightMessage', source: rightmessageSource },
  location: { label: 'Location', source: locationSource },
  date: { label: 'Date', source: dateSource },
  scores: { label: 'Scoring & Data', source: scoreSource },
  device: { label: 'Device', source: deviceSource },
  jsdata: { label: 'Browser Data', source: jsdataSource },
  wordpress: { label: 'WordPress', source: wordpressSource }
}

const availableSources = (context: LogicDefinitionContext) =>
  Object.entries(sources).filter(
    ([key]) => context.usageContext !== 'widget-targeting' || (key !== 'rightmessage' && key !== 'scores')
  )

const makeSourceFields = ({ partner, context }: { partner?: Partner; context: LogicDefinitionContext }) => {
  const partnerLabel = partner?.label || 'Email List'
  const partnerName = partner?.name || 'espplaceholder'

  return [
    ...[
      {
        group: partnerLabel,
        name: partnerName,
        options: makePartnerSource(partner).types.map((option) => ({
          ...option,
          value: `${partnerName}*${option.value}`
        }))
      }
    ],
    ...availableSources(context)
      .filter(([key]) => key !== 'answers')
      .map(([key, source]) => ({
        group: source.label,
        name: key,
        options: [
          ...source.source(context).types.map((option: { label: string; value: string }) => ({
            ...option,
            value: `${key}*${option.value}`
          })),
          ...(key === 'segments'
            ? answersSource().types.map((option) => ({ ...option, value: `answers*${option.value}` }))
            : [])
        ]
      }))
  ].filter((group) => {
    if (group.name === 'scores' && !context.offerFunnelId) {
      return false
    }
    return group.name !== 'segments' || group.options.length > 0
  })
}

const makeSchema = ({ partner, context }: { partner?: Partner; context: LogicDefinitionContext }) => [
  ...(partner ? makePartnerSource(partner).inputs : []),
  ...availableSources(context).flatMap(([, source]) => source.source(context).inputs)
]

export { makeSchema, makeSourceFields }
